// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Dashboard Calendar panel — Figma "Dashboard Calendar" (node 1827-19074).
 *
 * Replaces BOTH the old TodaysMeetingsWidget and the standalone /calendar
 * page: a month selector + Mon-start week strip + the selected day's
 * meetings, with Join Meeting and per-row overflow actions.
 *
 * Data: one fetch per visible week via the existing range API
 * (GET /integrations/calendar/today?from&to), cached per week for the
 * session. Navigation is bounded to −3 months … +2 months (PRD FR-6; the
 * backend sync window covers +60 days going forward, older events persist
 * in the cache once synced).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppIcon, Button, Dropdown, IconButton, Section, Skeleton, type DropdownOption } from '@loa/ui-components';
import { integrationsApi, type CalendarEventDTO, type CalendarConnectionStatusDTO } from '../../api/integrationsApi';
import { IntegrationReauthBanner } from '../integrations/IntegrationReauthBanner';
import { WeekStrip, addDays, isSameDay, startOfWeek } from './WeekStrip';
import './DashboardCalendarPanel.css';

const PAST_MONTHS = 3;
const FUTURE_MONTHS = 2;

function weekKey(d: Date): string {
  const ws = startOfWeek(d);
  return `${ws.getFullYear()}-${ws.getMonth()}-${ws.getDate()}`;
}

function formatTimeRange(event: CalendarEventDTO, selectedDate: Date): string {
  const start = event.startsAt ? new Date(event.startsAt) : null;
  const end = event.endsAt ? new Date(event.endsAt) : null;
  const fmt = (d: Date) => d.toLocaleTimeString('en-IE', { hour: 'numeric', minute: '2-digit', hour12: false });
  if (!start) return 'Time TBC';
  const range = end ? `${fmt(start)} - ${fmt(end)}` : fmt(start);
  return isSameDay(selectedDate, new Date()) ? `Today at ${range}` : range;
}

function MeetingRowMenu({ event }: Readonly<{ event: CalendarEventDTO }>) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const items: Array<{ key: string; label: string; action: () => void }> = [];
  event.linkedClients.forEach((client, index) => {
    items.push({
      key: `client-${client.clientProfileId}`,
      label: event.linkedClients.length > 1 ? `View client ${index + 1}` : 'View client',
      action: () => navigate(`/clients/${client.clientProfileId}`),
    });
  });
  if (event.onlineMeeting && event.onlineMeetingUrl) {
    items.push({
      key: 'copy-link',
      label: 'Copy meeting link',
      action: () => { void navigator.clipboard?.writeText(event.onlineMeetingUrl as string); },
    });
  }

  if (items.length === 0) return null;

  return (
    <div className="dashboard-calendar__row-menu" ref={menuRef}>
      <IconButton
        aria-label="Meeting actions"
        aria-expanded={open}
        variant="ghost"
        icon={<AppIcon name="more" />}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="dashboard-calendar__menu" role="menu">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className="dashboard-calendar__menu-item"
              onClick={() => { setOpen(false); item.action(); }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function DashboardCalendarPanel() {
  const today = useMemo(() => new Date(), []);
  const minDate = useMemo(() => {
    const d = new Date(today); d.setMonth(d.getMonth() - PAST_MONTHS); return d;
  }, [today]);
  const maxDate = useMemo(() => {
    const d = new Date(today); d.setMonth(d.getMonth() + FUTURE_MONTHS); return d;
  }, [today]);

  const [selectedDate, setSelectedDate] = useState<Date>(today);
  // Work week (Mon–Fri) by default; expandable to the full week. Auto-expands
  // when a weekend day becomes selected (e.g. via a month jump landing on a
  // Saturday 1st), so the selected pill can never be invisible.
  const [showWeekend, setShowWeekend] = useState(false);
  const [status, setStatus] = useState<CalendarConnectionStatusDTO | null>(null);
  const [weekEvents, setWeekEvents] = useState<Record<string, CalendarEventDTO[]>>({});
  const [statusLoading, setStatusLoading] = useState(true);
  const [weekLoading, setWeekLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStatusLoading(true);
        setError(null);
        const connectionStatus = await integrationsApi.getConnectionStatus();
        if (!cancelled) setStatus(connectionStatus);
      } catch {
        // Unreachable communications surface (feature flag off, service
        // down) reads as "not connected" — the connect empty state — not
        // as an error banner on every advisor's dashboard.
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadNonce]);

  const currentWeekKey = weekKey(selectedDate);
  const connected = !!status && status.hasConnection && !status.needsReauth;

  useEffect(() => {
    if (!connected || weekEvents[currentWeekKey]) return;
    let cancelled = false;
    (async () => {
      try {
        setWeekLoading(true);
        setError(null);
        const from = startOfWeek(selectedDate);
        const to = addDays(from, 7);
        const list = await integrationsApi.getTodaysMeetings({
          from: from.toISOString(),
          to: to.toISOString(),
        });
        if (!cancelled) {
          setWeekEvents((prev) => ({ ...prev, [currentWeekKey]: list }));
        }
      } catch {
        if (!cancelled) setError('Could not load meetings for this week — please try again later.');
      } finally {
        if (!cancelled) setWeekLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, currentWeekKey, reloadNonce]);

  const monthOptions: DropdownOption[] = useMemo(() => {
    const options: DropdownOption[] = [];
    const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    while (cursor <= end) {
      const label = cursor.toLocaleDateString('en-IE',
        cursor.getFullYear() === today.getFullYear() ? { month: 'long' } : { month: 'long', year: 'numeric' });
      options.push({ value: `${cursor.getFullYear()}-${cursor.getMonth()}`, text: label });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return options;
  }, [minDate, maxDate, today]);

  const selectedMonthValue = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}`;

  const handleMonthChange = (value: string) => {
    const [year, month] = value.split('-').map(Number);
    let target = new Date(year, month, 1);
    if (target < minDate) target = new Date(minDate);
    if (target > maxDate) target = new Date(maxDate);
    selectDate(target);
  };

  const selectDate = (date: Date) => {
    if (date.getDay() === 0 || date.getDay() === 6) {
      setShowWeekend(true);
    }
    setSelectedDate(date);
  };

  const isWeekendSelected = selectedDate.getDay() === 0 || selectedDate.getDay() === 6;

  const dayEvents = (weekEvents[currentWeekKey] ?? [])
    .filter((e) => e.startsAt && isSameDay(new Date(e.startsAt), selectedDate))
    .sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''));

  const renderBody = () => {
    if (statusLoading) {
      return <Skeleton lines={4} />;
    }
    if (error) {
      return (
        <div className="dashboard-calendar__error">
          <p>{error}</p>
          <Button variant="outlined" onClick={() => setReloadNonce((n) => n + 1)}>Try again</Button>
        </div>
      );
    }
    if (!status || !status.hasConnection) {
      return (
        <div className="dashboard-calendar__empty">
          <AppIcon name="calendar" />
          <p>Sign in with Google to see your meetings here.</p>
        </div>
      );
    }
    if (status.needsReauth) {
      return <IntegrationReauthBanner provider={status.provider ?? 'GOOGLE'} />;
    }

    return (
      <>
        <WeekStrip
          selectedDate={selectedDate}
          onSelectDate={selectDate}
          minDate={minDate}
          maxDate={maxDate}
          showWeekend={showWeekend}
        />
        {weekLoading && !weekEvents[currentWeekKey]
          ? <Skeleton lines={3} />
          : (
            <ul className="dashboard-calendar__list">
              {dayEvents.length === 0 && (
                <li className="dashboard-calendar__no-meetings">No meetings on this day.</li>
              )}
              {dayEvents.map((event) => (
                <li
                  key={event.eventId}
                  className={event.cancelled
                    ? 'dashboard-calendar__row dashboard-calendar__row--cancelled'
                    : 'dashboard-calendar__row'}
                >
                  <div className="dashboard-calendar__row-body">
                    <p className="dashboard-calendar__subject">{event.subject ?? '(No subject)'}</p>
                    <p className="dashboard-calendar__time">{formatTimeRange(event, selectedDate)}</p>
                  </div>
                  <div className="dashboard-calendar__row-actions">
                    {event.onlineMeeting && event.onlineMeetingUrl && (
                      <Button
                        variant="outlined"
                        onClick={() => window.open(event.onlineMeetingUrl as string, '_blank', 'noopener')}
                      >
                        Join Meeting
                      </Button>
                    )}
                    <MeetingRowMenu event={event} />
                  </div>
                </li>
              ))}
            </ul>
          )}
      </>
    );
  };

  return (
    <Section raised className="dashboard-calendar" data-testid="dashboard-calendar-panel">
      <div className="dashboard-calendar__header">
        <h2 className="dashboard-calendar__title">Calendar</h2>
        {connected && (
          <>
            <Dropdown
              variant="inline"
              aria-label="Month"
              options={monthOptions}
              value={selectedMonthValue}
              onChange={handleMonthChange}
            />
            <button
              type="button"
              className="dashboard-calendar__weekend-toggle"
              onClick={() => {
                if (showWeekend && isWeekendSelected) {
                  // Keep the selected pill visible: snap to that week's Friday.
                  const friday = addDays(selectedDate, selectedDate.getDay() === 6 ? -1 : -2);
                  setSelectedDate(friday);
                }
                setShowWeekend((v) => !v);
              }}
            >
              {showWeekend ? 'Hide weekend' : 'Show weekend'}
            </button>
          </>
        )}
      </div>
      {renderBody()}
    </Section>
  );
}
